import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaStethoscope, FaCalendar, FaVideo, FaUsers, FaChartLine, FaMobile, FaHeart, FaUserMd, FaClipboardList, FaPhoneAlt, FaHome, FaComments, FaTachometerAlt, FaDownload } from 'react-icons/fa';
import CustomButton from '../components/common/CustomButton';
import CustomAlert from '../components/common/CustomAlert';
import { AUTH_TOKEN_KEY } from '../utils/constants';
import logo from '../assets/images/logo.png';
import './IntroPage.css';

const IntroPage = () => {
  const navigate = useNavigate();
  const isLoggedIn = localStorage.getItem(AUTH_TOKEN_KEY);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ isOpen: false, title: '', message: '', type: 'info' });
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    // Check if PWA is installed and user is logged in
    const isPWAInstalled = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (isPWAInstalled && isLoggedIn) {
      navigate('/dashboard');
      return;
    }else if(isPWAInstalled && !isLoggedIn) {
      navigate('/login');
    }
    
    // Check if mobile app is installed
    checkAppInstalled();
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    // Re-check installation status periodically
    const interval = setInterval(() => {
      checkAppInstalled();
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  const checkAppInstalled = () => {
    // Check if PWA is installed using multiple methods
    const isPWAInstalled = window.matchMedia('(display-mode: standalone)').matches || 
                          window.navigator.standalone || 
                          document.referrer.includes('android-app://');
    
    // If not installed but localStorage says it was, user uninstalled it
    if (!isPWAInstalled && localStorage.getItem('pwa-installed') === 'true') {
      localStorage.removeItem('pwa-installed');
    }
    
    // Only trust localStorage if browser also confirms installation
    const finalStatus = isPWAInstalled || (isPWAInstalled && localStorage.getItem('pwa-installed') === 'true');
    setIsAppInstalled(finalStatus);
  };

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    const handleAppInstalled = () => {
      setShowInstallButton(false);
      setDeferredPrompt(null);
      setIsInstalling(false);
      setIsAppInstalled(true);
      localStorage.setItem('pwa-installed', 'true');
      
      // Auto open app after installation
      setTimeout(() => {
        window.open(window.location.origin, '_blank');
      }, 500);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const showAlert = (title, message, type = 'info') => {
    setAlertConfig({ isOpen: true, title, message, type });
  };

  const closeAlert = () => {
    setAlertConfig({ ...alertConfig, isOpen: false });
  };

  const handleInstallPWA = async () => {
    if (deferredPrompt) {
      setIsInstalling(true);
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        localStorage.setItem('pwa-installed', 'true');
        setIsAppInstalled(true);
      } else {
        setIsInstalling(false);
      }
      
      setDeferredPrompt(null);
      setShowInstallButton(false);
    } else {
      if (isAppInstalled) {
        showAlert('Already Installed', 'App is already installed on your device!', 'info');
      } else {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isAndroid = /Android/.test(navigator.userAgent);
        
        if (isIOS) {
          showAlert('Install Instructions', 'To install: Tap the Share button in Safari, then "Add to Home Screen"', 'info');
        } else if (isAndroid) {
          showAlert('Install Instructions', 'To install: Tap the menu (⋮) in Chrome, then "Add to Home screen" or "Install app"', 'info');
        } else {
          showAlert('Install Instructions', 'To install: Look for the install icon in your browser\'s address bar or menu', 'info');
        }
      }
    }
  };

  const handleOpenApp = () => {
    if (isInstalling) {
      return; // Do nothing while installing
    }
    
    if (isAppInstalled) {
      // PWA is installed, open it
      window.open(window.location.origin, '_blank');
    } else {
      // PWA not installed, show install option
      handleInstallPWA();
    }
  };

  const features = [
    {
      icon: <FaCalendar />,
      title: 'Smart Scheduling',
      description: 'Intelligent appointment booking with automated reminders and calendar sync'
    },
    {
      icon: <FaVideo />,
      title: 'Video Consultations',
      description: 'High-quality video calls with secure patient communication'
    },
    {
      icon: <FaPhoneAlt />,
      title: 'Tele Consultations',
      description: 'Audio consultations for quick patient follow-ups and advice'
    },
    {
      icon: <FaHome />,
      title: 'Home Visits',
      description: 'Manage and schedule home visits for personalized patient care'
    },
    {
      icon: <FaUsers />,
      title: 'Patient Records',
      description: 'Comprehensive patient management with medical history tracking'
    },
    {
      icon: <FaChartLine />,
      title: 'Practice Analytics',
      description: 'Real-time insights into your practice performance and growth'
    }
  ];

  const consultationTypes = [
    {
      icon: <FaStethoscope />,
      title: 'In-Clinic Appointments',
      description: 'Traditional face-to-face consultations in your clinic'
    },
    {
      icon: <FaHome />,
      title: 'Home Visits',
      description: 'Personalized care at patient\'s home or preferred location'
    },
    {
      icon: <FaVideo />,
      title: 'Video Consultations',
      description: 'Remote consultations through secure video calls'
    },
    {
      icon: <FaPhoneAlt />,
      title: 'Phone Consultations',
      description: 'Quick consultations and follow-ups via phone calls'
    },
    {
      icon: <FaComments />,
      title: 'Chat Consultations',
      description: 'Text-based consultations for non-urgent medical advice'
    }
  ];

  return (
    <div className="intro-page">
      <header className="intro-header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <img src={logo} alt="MeetUrExpert For Professionals" className="logo-image" />
              <div className="logo-text">
                <h1>MeetUrExpert</h1>
                <span>For Professionals</span>
              </div>
            </div>
            <nav className="nav-links">
              {isLoggedIn ? (
                <button onClick={() => navigate('/dashboard')} className="nav-link dashboard-btn">
                  <FaTachometerAlt /> Dashboard
                </button>
              ) : (
                <button onClick={() => navigate('/login')} className="nav-link">
                  Login
                </button>
              )}
            </nav>
          </div>
        </div>
      </header>

      <main className="intro-main">
        <section className="hero-section">
          <div className="container">
            <div className="hero-content">
              <div className="hero-text">
                <h2>Transform Your Healthcare Practice</h2>
                <p>
                  Empower your medical practice with our comprehensive digital platform. 
                  Manage appointments, conduct consultations, and provide exceptional patient care 
                  from anywhere, anytime.
                </p>
                <div className="hero-stats">
                  <div className="stat">
                    <span className="stat-number-intro">5000+</span>
                    <span className="stat-label-intro">Healthcare Professionals</span>
                  </div>
                  <div className="stat">
                    <span className="stat-number-intro">50K+</span>
                    <span className="stat-label-intro">Appointments Managed</span>
                  </div>
                  <div className="stat">
                    <span className="stat-number-intro">98%</span>
                    <span className="stat-label-intro">Patient Satisfaction</span>
                  </div>
                </div>
                <div className="hero-actions">
                  {isLoggedIn ? (
                    <CustomButton
                      title="Go to Dashboard"
                      onPress={() => navigate('/dashboard')}
                      size="large"
                    />
                  ) : (
                    <CustomButton
                      title="Start Your Practice Journey"
                      onPress={() => navigate('/login')}
                      size="large"
                    />
                  )}
                  <button className="demo-btn" onClick={handleOpenApp}>
                    {isInstalling ? <FaDownload /> : (isAppInstalled ? <FaMobile /> : <FaDownload />)} 
                    {isInstalling ? 'Installing...' : (isAppInstalled ? 'Open Mobile App' : 'Install Mobile App')} : 'Install Mobile App')}
                  </button>
                </div>
              </div>
              <div className="hero-image">
                <div className="hero-visual">
                  <div className="floating-card card-1">
                    <FaCalendar className="card-icon" />
                    <span>Today: 12 Appointments</span>
                  </div>
                  <div className="floating-card card-2">
                    <FaVideo className="card-icon" />
                    <span>3 Video Calls</span>
                  </div>
                  <div className="floating-card card-3">
                    <FaUsers className="card-icon" />
                    <span>248 Patients</span>
                  </div>
                  <div className="hero-logo">
                    <img src={logo} alt="MeetUrExpert" className="hero-logo-img" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="consultation-types-section">
          <div className="container">
            <div className="section-header">
              <h3>Multiple Consultation Options</h3>
              <p>Offer flexible healthcare services that meet your patients' diverse needs</p>
            </div>
            <div className="consultation-grid">
              {consultationTypes.map((type, index) => (
                <div key={index} className="consultation-card">
                  <div className="consultation-icon">
                    {type.icon}
                  </div>
                  <h4>{type.title}</h4>
                  <p>{type.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="features-section">
          <div className="container">
            <div className="section-header">
              <h3>Powerful Features for Modern Healthcare</h3>
              <p>Everything you need to run a successful medical practice</p>
            </div>
            <div className="features-grid">
              {features.map((feature, index) => (
                <div key={index} className="feature-card">
                  <div className="feature-icon">
                    {feature.icon}
                  </div>
                  <h4>{feature.title}</h4>
                  <p>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="testimonial-section">
          <div className="container">
            <div className="testimonial-content">
              <div className="testimonial-header">
                <h3>Trusted by Healthcare Professionals</h3>
                <p>See what doctors are saying about MeetUrExpert</p>
              </div>
              <div className="testimonial-card">
                <div className="testimonial-text">
                  <FaHeart className="quote-icon" />
                  <p>"MeetUrExpert has revolutionized how I manage my practice. The seamless appointment scheduling and video consultation features have made patient care more efficient and accessible."</p>
                </div>
                <div className="testimonial-author">
                  <div className="author-avatar">
                    <FaUserMd />
                  </div>
                  <div className="author-info">
                    <h5>Dr. Sarah Johnson</h5>
                    <span>Orthopedic Specialist</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <div className="container">
            <div className="cta-content">
              <div className="cta-text">
                <h3>Ready to Elevate Your Healthcare Practice?</h3>
                <p>Join thousands of healthcare professionals who are already transforming patient care with MeetUrExpert</p>
              </div>
              <div className="cta-actions">
                {isLoggedIn ? (
                  <CustomButton
                    title="Go to Dashboard"
                    onPress={() => navigate('/dashboard')}
                    size="large"
                  />
                ) : (
                  <CustomButton
                    title="Get Started Today"
                    onPress={() => navigate('/login')}
                    size="large"
                  />
                )}
                <div className="cta-features">
                  <div className="cta-feature">
                    <FaClipboardList className="cta-feature-icon" />
                    <span>Free Setup</span>
                  </div>
                  <div className="cta-feature">
                    <FaHeart className="cta-feature-icon" />
                    <span>24/7 Support</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="intro-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <img src={logo} alt="MeetUrExpert For Professionals" className="footer-logo-img" />
              <div className="footer-logo-text">
                <h4>MeetUrExpert</h4>
                <p>Empowering Healthcare Professionals</p>
              </div>
            </div>
            <div className="footer-links">
              <div className="footer-section">
                <h5>Platform</h5>
                <ul>
                  <li><button onClick={() => navigate('/dashboard')} className="footer-link">Dashboard</button></li>
                  <li><button onClick={() => navigate('/appointments')} className="footer-link">Appointments</button></li>
                  <li><button onClick={() => navigate('/profile')} className="footer-link">Profile</button></li>
                </ul>
              </div>
              <div className="footer-section">
                <h5>Support</h5>
                <ul>
                  <li><button onClick={() => navigate('/about-us')} className="footer-link">About Us</button></li>
                  <li><button onClick={() => navigate('/contact-us')} className="footer-link">Contact Us</button></li>
                  <li><button onClick={() => navigate('/faq')} className="footer-link">Training</button></li>
                </ul>
              </div>
              <div className="footer-section">
                <h5>Legal</h5>
                <ul>
                  <li><button onClick={() => navigate('/privacy-policy')} className="footer-link">Privacy Policy</button></li>
                  <li><button onClick={() => navigate('/terms-and-conditions')} className="footer-link">Terms of Service</button></li>
                  <li><button onClick={() => navigate('/faq')} className="footer-link">FAQ</button></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <div className="mobile-footer-links">
              <button onClick={() => navigate('/faq')} className="mobile-footer-link">FAQ</button>
              <button onClick={() => navigate('/privacy-policy')} className="mobile-footer-link">Privacy Policy</button>
              <button onClick={() => navigate('/terms-and-conditions')} className="mobile-footer-link">Terms & Conditions</button>
              <button onClick={() => navigate('/contact-us')} className="mobile-footer-link">Contact Us</button>
            </div>
            <p>&copy; 2024 MeetUrExpert Expert. All rights reserved. | Expert Care.Anytime.Anywhere</p>
          </div>
        </div>
      </footer>
      
      <CustomAlert
        isOpen={alertConfig.isOpen}
        onClose={closeAlert}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
      />
      
      {isInstalling && (
        <div className="pwa-success-screen">
          <div className="success-content">
            <div className="success-animation">
              <div className="installing-spinner"></div>
            </div>
            <h2>Installing App...</h2>
            <p>Please wait while we install the app on your device.</p>
          </div>
        </div>
      )}
      
      {showSuccessScreen && (
        <div className="pwa-success-screen">
          <div className="success-content">
            <div className="success-animation">
              <div className="trophy">
                <div className="trophy-cup"></div>
                <div className="trophy-stem"></div>
                <div className="trophy-base"></div>
              </div>
              <div className="confetti">
                <div className="confetti-piece confetti-1"></div>
                <div className="confetti-piece confetti-2"></div>
                <div className="confetti-piece confetti-3"></div>
                <div className="confetti-piece confetti-4"></div>
                <div className="confetti-piece confetti-5"></div>
                <div className="confetti-piece confetti-6"></div>
                <div className="confetti-piece confetti-7"></div>
                <div className="confetti-piece confetti-8"></div>
                <div className="confetti-piece confetti-9"></div>
                <div className="confetti-piece confetti-10"></div>
              </div>
              <div className="paper paper-1"></div>
              <div className="paper paper-2"></div>
              <div className="paper paper-3"></div>
              <div className="paper paper-4"></div>
              <div className="paper paper-5"></div>
              <div className="paper paper-6"></div>
              <div className="paper paper-7"></div>
              <div className="paper paper-8"></div>
              <div className="sparkle sparkle-1"></div>
              <div className="sparkle sparkle-2"></div>
              <div className="sparkle sparkle-3"></div>
              <div className="sparkle sparkle-4"></div>
              <div className="sparkle sparkle-5"></div>
              <div className="sparkle sparkle-6"></div>
              <div className="burst-ring"></div>
              <div className="burst-ring-2"></div>
              <div className="ribbon">SUCCESS!</div>
            </div>
            <h2>Installation Successful!</h2>
            <p>Thank you for installing Expert for Professionals. You can now access the app directly from your home screen.</p>
            <p>App will open automatically...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntroPage;